
import { Component, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { format } from '@hokify/shared-components/lib/helpers/datehelpers/format';
import { truncateFct } from '@hokify/shared-components/lib/helpers/truncate';
import { Slide } from '@hokify/hooper';
import type {
	IAPILoginUser,
	IAPIPageSettings,
	IAPIWebinar,
	IAPIWebinarTeaser
} from '@hokify/common';
import LoginBase from '~/mixins/LoginBase';
import { getHeadFromPageSettings, getSocialMediaMetaData } from '~/helpers/metadata';

const contentStore = namespace('b2b-content');
const loginStore = namespace('login');
const userStore = namespace('user/profile');

@Component({
	name: 'WebinarsDetailPage',
	layout: 'plain-company',
	components: {
		CallToAction: () => import('@hokify/shared-components/lib/components/CallToAction.vue'),
		ItemList: () => import('~/components/website/list/ItemList.vue'),
		CheckPrivacyAlert: () => import('~/components/modals/CheckPrivacyAlert.vue'),
		HokCarousel: () => import('@hokify/shared-components/lib/components/HokCarousel.vue'),
		BeautifyText: () => import('@hokify/shared-components/lib/components/BeautifyText.vue'),
		Slide
	},
	methods: { truncateFct },
	head(this: WebinarsDetailPage) {
		const result: any = getHeadFromPageSettings(this.pageSettings);
		result.meta.push({
			hid: 'description',
			name: 'description',
			content: this.webinar.teaser
		});

		if (this.webinar.title) {
			result.title = this.webinar.title;
			if (!result.title.toLowerCase().includes('hokify')) {
				result.titleTemplate = '%s | hokify';
			}
		}
		result.meta.push(
			...getSocialMediaMetaData(
				result,
				this.$store.state.topLevelDomain,
				[this.webinar.images?.medium],
				undefined,
				this.$route.query
			)
		);
		return result;
	},
	async asyncData({ params, store, error }) {
		try {
			const [result] = await Promise.all([
				store.dispatch('b2b-content/loadWebinarDetailPageData', params.alias),
				store.dispatch('b2b-content/loadPastWebinars')
			]);
			return {
				pageSettings: result.pageSettings,
				alias: params.alias
			};
		} catch (err: any) {
			if (err.response) {
				error({
					statusCode: err.response.status,
					message:
						err.response.data && err.response.data.code
							? `${err.response.data.code}: ${err.response.data.msg || err.response.data.message}`
							: `Es ist ein Fehler aufgetreten${
									err.response.data ? ` (${JSON.stringify(err.response.data)})` : ''
								}`
				});
			} else {
				error(err);
			}
		}

		// LEAVE IN FOR FUTURE TESTING PURPOSES
		// console.log(webinar);
		// return {
		// 	webinar: {
		// 		title:
		// 			'Geheimnis der perfekten Candidate Journey: Conversion Rate Optimierung vs. Bewerberqualität',
		// 		alias:
		// 			'geheimnis-der-perfekten-candidate-journey-conversion-rate-optimierung-vs-bewerberqualitaet',
		// 		teaser:
		// 			'Haben Sie sich schon einmal gefragt bei welchem Schritt in Ihrem Bewerbungsprozess die meisten KandidatInnen abspringen? Wissen Sie welche Auswirkunge',
		// 		date: '2020-06-30T10:01:05Z',
		// 		images: {
		// 			thumbnail:
		// 				'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvNmM1MTZhOWUtMWI2ZS00NGFlLWIxZTAtY2E5NDc4M2JkZTEzLmpwZWciLCJlZGl0cyI6eyJyb3RhdGUiOm51bGwsInJlc2l6ZSI6eyJ3aWR0aCI6MTIsImZpdCI6Imluc2lkZSJ9fSwiaGFzaCI6IjA2NGJiY2JlNzA0Y2IyY2RkNTM0ZWY1NmViMWFkNDdjIn0=/6c516a9e-1b6e-44ae-b1e0-ca94783bde13.jpeg',
		// 			small:
		// 				'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvNmM1MTZhOWUtMWI2ZS00NGFlLWIxZTAtY2E5NDc4M2JkZTEzLmpwZWciLCJlZGl0cyI6eyJyb3RhdGUiOm51bGwsInJlc2l6ZSI6eyJ3aWR0aCI6MzAxLCJmaXQiOiJpbnNpZGUifX0sImhhc2giOiI0NDYwZmQwZDVlY2M2NzlhM2E4ODEzZGJiOWFhMWIzYSJ9/6c516a9e-1b6e-44ae-b1e0-ca94783bde13.jpeg',
		// 			medium:
		// 				'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvNmM1MTZhOWUtMWI2ZS00NGFlLWIxZTAtY2E5NDc4M2JkZTEzLmpwZWciLCJlZGl0cyI6eyJyb3RhdGUiOm51bGwsInJlc2l6ZSI6eyJ3aWR0aCI6NjAyLCJmaXQiOiJpbnNpZGUifX0sImhhc2giOiI5YmRlNDg2MWM1MmFmOGQ2NDgwN2ZhMzJhMjI0OTNjNCJ9/6c516a9e-1b6e-44ae-b1e0-ca94783bde13.jpeg',
		// 			large:
		// 				'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvNmM1MTZhOWUtMWI2ZS00NGFlLWIxZTAtY2E5NDc4M2JkZTEzLmpwZWciLCJlZGl0cyI6eyJyb3RhdGUiOm51bGwsInJlc2l6ZSI6eyJ3aWR0aCI6MTIwNCwiZml0IjoiaW5zaWRlIn19LCJoYXNoIjoiYTQzNzk5NmE4ZDg0MjIxZjQyMjg3MTJkYmNkMjdjMzkifQ==/6c516a9e-1b6e-44ae-b1e0-ca94783bde13.jpeg',
		// 			xlarge:
		// 				'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvNmM1MTZhOWUtMWI2ZS00NGFlLWIxZTAtY2E5NDc4M2JkZTEzLmpwZWciLCJlZGl0cyI6eyJyb3RhdGUiOm51bGwsInJlc2l6ZSI6eyJ3aWR0aCI6MTgwNiwiZml0IjoiaW5zaWRlIn19LCJoYXNoIjoiOTA1MDMzYjBhZjgxNzE5OTVhMGFmZjRkN2YyMjI4MWIifQ==/6c516a9e-1b6e-44ae-b1e0-ca94783bde13.jpeg'
		// 		},
		// 		imageAlt: 'Webinar banner',
		// 		published: true,
		// 		startAt: '2020-07-21T08:00:00Z',
		// 		duration: 60,
		// 		type: 'upcoming',
		// 		content:
		// 			'Haben Sie sich schon einmal gefragt bei welchem Schritt in Ihrem Bewerbungsprozess die meisten KandidatInnen abspringen? Wissen Sie welche Auswirkungen ein verpflichtendes Foto auf die Wahrscheinlichkeit einer Bewerbung hat oder wie Dokumenten Uploads Ihre BewerberInnen-Anzahl beeinflussen? Und erhalten Sie aktuell mit Ihren Bewerbungsunterlagen wirklich alle wichtigen Informationen, um einschätzen zu können, ob ein/e KandidatIn passt oder nicht?\n \nIn diesem Webinar analysieren wir die einzelnen Schritte bei der Bewerbungserstellung und zeigen Ihnen welche Abfragen Ihre KandidatInnen abschrecken. Weiters stellen wir Ihnen Möglichkeiten vor, um die Qualifikationen von BewerberInnen mobile-friendly abzufragen und gleichzeitig die Bewerberqualität zu optimieren. Melden Sie sich jetzt kostenlos zum Webinar mit hokify Co-Founder Karl Edlbauer an und werden Sie zum Candidate Journey und Applicant Conversion Optimization Experten.',
		// 		registrationUrl: 'https://us02web.zoom.us/webinar/register/WN_Q8qnI2cFT5yWgh62NvnTHA',
		// 		joinUrl: 'https://us02web.zoom.us/j/84968995316',
		// 		endAt: '2020-07-21T08:00:00.060Z',
		// 		speakers: [
		// 			{
		// 				displayName: 'Karl Edlbauer',
		// 				jobTitle: 'Co-Founder & Geschäftsführer',
		// 				images: {
		// 					thumbnail:
		// 						'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvMTIwMWY2NWItMGM5OS00MTk1LTk5ZWUtMWU2NTlkYjRhZWUxLmpwZyIsImVkaXRzIjp7InJvdGF0ZSI6bnVsbCwicmVzaXplIjp7IndpZHRoIjoxMiwiZml0IjoiaW5zaWRlIn19LCJoYXNoIjoiZWMwZjUzOThhZjViNDdlNDFkYTdlYmZmYjI3MGFjNGIifQ==/1201f65b-0c99-4195-99ee-1e659db4aee1.jpg',
		// 					small:
		// 						'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvMTIwMWY2NWItMGM5OS00MTk1LTk5ZWUtMWU2NTlkYjRhZWUxLmpwZyIsImVkaXRzIjp7InJvdGF0ZSI6bnVsbCwicmVzaXplIjp7IndpZHRoIjoyMjUsImZpdCI6Imluc2lkZSJ9fSwiaGFzaCI6Ijk1MjQ2NDQ2YmY2ODBkZTYwYzQzYzEwNGI3NjdlNDk5In0=/1201f65b-0c99-4195-99ee-1e659db4aee1.jpg',
		// 					medium:
		// 						'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvMTIwMWY2NWItMGM5OS00MTk1LTk5ZWUtMWU2NTlkYjRhZWUxLmpwZyIsImVkaXRzIjp7InJvdGF0ZSI6bnVsbCwicmVzaXplIjp7IndpZHRoIjo0NTAsImZpdCI6Imluc2lkZSJ9fSwiaGFzaCI6ImY5ZTkxYTRjMzlmY2VjY2VhN2EzNTEyZDY2OWVmOGMxIn0=/1201f65b-0c99-4195-99ee-1e659db4aee1.jpg',
		// 					large:
		// 						'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvMTIwMWY2NWItMGM5OS00MTk1LTk5ZWUtMWU2NTlkYjRhZWUxLmpwZyIsImVkaXRzIjp7InJvdGF0ZSI6bnVsbCwicmVzaXplIjp7IndpZHRoIjo5MDAsImZpdCI6Imluc2lkZSJ9fSwiaGFzaCI6ImU4Y2FiYjg1ZjdmMzNjODI2NTMyNDFlMDliOTg0NGRjIn0=/1201f65b-0c99-4195-99ee-1e659db4aee1.jpg',
		// 					xlarge:
		// 						'https://img.hokify.com/eyJidWNrZXQiOiJmaWxlcy10ZXN0Lmhva2lmeS5jb20iLCJrZXkiOiJodHRwczovL3VzMDJ3ZWIuem9vbS51cy93X3AvODQ5Njg5OTUzMTYvMTIwMWY2NWItMGM5OS00MTk1LTk5ZWUtMWU2NTlkYjRhZWUxLmpwZyIsImVkaXRzIjp7InJvdGF0ZSI6bnVsbCwicmVzaXplIjp7IndpZHRoIjoxMzUwLCJmaXQiOiJpbnNpZGUifX0sImhhc2giOiJkMWFiMTk0ZjQwYmYzZTQ4ZWExYmZjODI4NDE5NmMyZSJ9/1201f65b-0c99-4195-99ee-1e659db4aee1.jpg'
		// 				},
		// 				biography:
		// 					'Karl Edlbauer gründete 2015 mit zwei Studienkollegen die mobile Job-Plattform hokify und ist seither als Geschäftsführer und Chief Marketing Officer tätig. Während seines Master-Studiums in Wien sammelte Karl Erfahrung bei der Boston Consulting Group, IBM und der Hofer KG. Seit der Gründung führte Karl als Geschäftsführer und CMO hokify auf 700.000 aktive monatliche Nutzer in Österreich und Deutschland und entwickelte einen neuartigen Social Media Algorithmus zur Ansprache von Fachkräften via Smartphone und Social Media.'
		// 			}
		// 		]
		// 	},
		// 	pastWebinars: pastWebinars.webinars,
		// 	webinarType: 'upcoming',
		// 	alias: params.alias
		// };
	}
})
export default class WebinarsDetailPage extends Mixins(LoginBase({ cref: 'digital-content' })) {
	@contentStore.Action('registerForWebinar') registerForWebinar;

	@contentStore.Action('getWebinarVideoLink') getWebinarVideoLink;

	@contentStore.Getter('getWebinarByAlias') getWebinarByAlias;

	@contentStore.State('pastWebinarTeaser') pastWebinarTeaser;

	@loginStore.State('loggedIn') loggedIn;

	@userStore.State('obj') user!: IAPILoginUser;

	mode = 'login';

	pageSettings!: IAPIPageSettings;

	alias!: string;

	signedUp = false;

	format = format;

	get webinar(): IAPIWebinar {
		return this.getWebinarByAlias(this.alias);
	}

	get largeSpeakerGroup(): boolean {
		return !!(this.webinar.speakers && this.webinar.speakers.length > 9);
	}

	get pastWebinars(): IAPIWebinarTeaser[] {
		return this.pastWebinarTeaser.filter(webinar => webinar.alias !== this.alias);
	}

	get webinarType(): 'upcoming' | 'past' {
		return this.webinar.type;
	}

	openYtVideo(link: string): void {
		this.$openLink(this.webinar.title, link, 'media-detail');
	}

	handleButtonClick() {
		if (this.loggedIn && !this.user?.privacy?.company_digital_content) {
			this.$modal.show('checkPrivacyAlert');
		} else if (this.loggedIn) {
			if (this.webinarType === 'upcoming') {
				this.doRegistration();
			} else {
				this.getVideoLink();
			}
		} else {
			this.setupContentForm();
		}
	}

	handleSendStatus(message: string) {
		switch (message) {
			case 'success':
				this.$modal.hide('checkPrivacyAlert');
				if (this.webinarType === 'upcoming') {
					this.doRegistration();
				} else {
					this.getVideoLink();
				}
				break;
			case 'abort':
			case 'fail':
			default:
				this.$snack.danger({
					title: 'Registrierung fehlgeschlagen!',
					text: 'Bei der Registrierung ist etwas schief gelaufen.'
				});
				break;
		}
	}

	async doRegistration() {
		try {
			await this.registerForWebinar(this.alias);
			this.signedUp = true;
			this.$trackWebsiteEvent('webinar_register_completed', {});
			this.$trackRecruiterEvent({ item: this.alias }, 'webinar');
			this.$snack.success({
				text: 'Sie wurden erfolgreich für das Webinar angemeldet!'
			});
		} catch (err: any) {
			if (err.response?.status === 401 && err.response?.data?.code === 'PRECONDITION_REQUIRED') {
				this.$modal.show('checkPrivacyAlert');
			} else {
				this.$errorHandler.call(
					this,
					err,
					'Bei der Anmeldung zum Webinar ist etwas schief gelaufen.'
				);
			}
		}
	}

	async getVideoLink() {
		try {
			const link = await this.getWebinarVideoLink(this.alias);
			this.openYtVideo(link.url);
		} catch (err: any) {
			this.$errorHandler.call(this, err, 'Das Video konnte nicht aufgerufen werden.');
		}
	}

	get formattedDate() {
		return format('dd.MM.yyyy', this.webinar.startAt);
	}

	speakerImage(speaker) {
		// eslint-disable-next-line global-require
		return speaker?.images || require('~/assets/img/unknown_user.png');
	}

	schemaData() {
		const data = {
			'@context': 'http://schema.org/',
			'@type': 'Event',
			name: this.webinar.title,
			location: {
				'@type': 'VirtualLocation',
				url: this.webinar.joinUrl
			},
			startDate: this.webinar.startAt,
			description: this.webinar.content,
			endDate: this.webinar.endAt,
			eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
			eventStatus: 'https://schema.org/EventScheduled',
			image: this.webinar.images?.medium,
			organizer: {
				'@type': 'Organization',
				name: 'hokify',
				url: `https://hokify.${this.$store.state.topLevelDomain}`
			},
			performer: this.webinar.speakers?.map(speaker => ({
				'@type': 'Person',
				name: speaker.displayName
			}))
		};

		return JSON.stringify(data);
	}
}
